import React, { useState, useEffect } from 'react';
import { AppLayout } from '../../components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, Users, Flag, Award, DollarSign, Settings, 
  CheckCircle, XCircle, Eye, Ban, AlertTriangle, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminConnectPage() {
  const [activeTab, setActiveTab] = useState('reports');
  const [reports, setReports] = useState([]);
  const [verificationApps, setVerificationApps] = useState([]);
  const [rewardSettings, setRewardSettings] = useState({
    enabled: true,
    likes_threshold: 100,
    reward_amount: 10,
    max_rewards_per_day: 50
  });
  const [aiSettings, setAiSettings] = useState({
    enabled: true,
    daily_limit_free: 10,
    daily_limit_premium: -1
  });
  const [aiStats, setAiStats] = useState(null);
  
  // Creator Revenue States
  const [creatorConfig, setCreatorConfig] = useState({
    enabled: true,
    ad_revenue_per_1000_views: 1.0,
    likes_bonus_per_100: 0.5,
    comments_bonus_per_10: 0.3,
    shares_bonus_per_10: 0.5,
    tip_revenue_share: 0.90,
    minimum_payout: 10.0
  });
  const [topCreators, setTopCreators] = useState([]);
  
  // Referral Revenue States
  const [referralConfig, setReferralConfig] = useState({
    enabled: true,
    transaction_commission: 0.05,
    deposit_commission: 0.02,
    purchase_commission: 0.10,
    signup_bonus: 5.0,
    first_deposit_bonus: 10.0,
    lifetime_commissions: true,
    minimum_payout: 50.0
  });
  const [topReferrers, setTopReferrers] = useState([]);
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'reports') loadReports();
    if (activeTab === 'verification') loadVerificationApps();
    if (activeTab === 'rewards') loadRewardSettings();
    if (activeTab === 'ai') loadAISettings();
    if (activeTab === 'creator') loadCreatorSettings();
    if (activeTab === 'referral') loadReferralSettings();
  }, [activeTab]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/moderation/admin/reports?status=pending`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || []);
      }
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadVerificationApps = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/verification/admin/applications?status=pending`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setVerificationApps(data.applications || []);
      }
    } catch (error) {
      console.error('Failed to load applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRewardSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/config`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.config?.engagement_rewards) {
          setRewardSettings(data.config.engagement_rewards);
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadAISettings = async () => {
    try {
      setLoading(true);
      const [settingsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/ai/admin/settings`, { credentials: 'include' }),
        fetch(`${API_URL}/api/ai/admin/usage-stats?days=7`, { credentials: 'include' })
      ]);
      
      if (settingsRes.ok) {
        const settings = await settingsRes.json();
        setAiSettings(settings);
      }
      
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setAiStats(stats);
      }
    } catch (error) {
      console.error('Failed to load AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateAISettings = async (updates) => {
    try {
      const res = await fetch(`${API_URL}/api/ai/admin/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        const data = await res.json();
        setAiSettings(data.settings);
        toast.success('AI settings updated');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const loadCreatorSettings = async () => {
    try {
      setLoading(true);
      const [configRes, creatorsRes] = await Promise.all([
        fetch(`${API_URL}/api/creator/admin/config`, { credentials: 'include' }),
        fetch(`${API_URL}/api/creator/admin/top-creators?limit=20`, { credentials: 'include' })
      ]);
      
      if (configRes.ok) {
        const config = await configRes.json();
        setCreatorConfig(config);
      }
      
      if (creatorsRes.ok) {
        const data = await creatorsRes.json();
        setTopCreators(data.creators || []);
      }
    } catch (error) {
      console.error('Failed to load creator settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCreatorSettings = async (updates) => {
    try {
      const res = await fetch(`${API_URL}/api/creator/admin/config`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        const data = await res.json();
        setCreatorConfig(data.config);
        toast.success('Creator settings updated');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const loadReferralSettings = async () => {
    try {
      setLoading(true);
      const [configRes, referrersRes] = await Promise.all([
        fetch(`${API_URL}/api/referral-revenue/admin/config`, { credentials: 'include' }),
        fetch(`${API_URL}/api/referral-revenue/admin/top-referrers?limit=20`, { credentials: 'include' })
      ]);
      
      if (configRes.ok) {
        const config = await configRes.json();
        setReferralConfig(config);
      }
      
      if (referrersRes.ok) {
        const data = await referrersRes.json();
        setTopReferrers(data.referrers || []);
      }
    } catch (error) {
      console.error('Failed to load referral settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateReferralSettings = async (updates) => {
    try {
      const res = await fetch(`${API_URL}/api/referral-revenue/admin/config`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        const data = await res.json();
        setReferralConfig(data.config);
        toast.success('Referral settings updated');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleModerate = async (reportId, action, notes = '') => {
    try {
      const res = await fetch(`${API_URL}/api/moderation/admin/moderate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_id: reportId,
          action,
          notes,
          duration_days: action === 'suspend' ? 7 : undefined
        })
      });

      if (res.ok) {
        toast.success(`Action taken: ${action}`);
        loadReports();
      } else {
        toast.error('Failed to moderate');
      }
    } catch (error) {
      toast.error('Failed to moderate');
    }
  };

  const handleVerificationReview = async (appId, approved, notes = '') => {
    try {
      const res = await fetch(`${API_URL}/api/verification/admin/review`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: appId,
          approved,
          admin_notes: notes
        })
      });

      if (res.ok) {
        toast.success(approved ? 'Application approved' : 'Application rejected');
        loadVerificationApps();
      } else {
        toast.error('Failed to review');
      }
    } catch (error) {
      toast.error('Failed to review');
    }
  };

  const saveRewardSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/config`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          engagement_rewards: rewardSettings
        })
      });

      if (res.ok) {
        toast.success('Reward settings saved');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Matrise Connect Admin</h1>
            <p className="text-gray-600">Moderation, verification & settings</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="reports">
              <Flag className="w-4 h-4 mr-2" />
              Reports
              {reports.length > 0 && (
                <Badge variant="destructive" className="ml-2">{reports.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="verification">
              <Award className="w-4 h-4 mr-2" />
              Verification
              {verificationApps.length > 0 && (
                <Badge className="ml-2">{verificationApps.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="rewards">
              <DollarSign className="w-4 h-4 mr-2" />
              Rewards
            </TabsTrigger>
            <TabsTrigger value="ai">
              <Sparkles className="w-4 h-4 mr-2" />
              AI Control
            </TabsTrigger>
            <TabsTrigger value="creator">
              <Sparkles className="w-4 h-4 mr-2" />
              Creator Revenue
            </TabsTrigger>
            <TabsTrigger value="referral">
              <Users className="w-4 h-4 mr-2" />
              Referral Revenue
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Pending Reports</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center py-8 text-gray-500">Loading...</p>
                ) : reports.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No pending reports</p>
                ) : (
                  <div className="space-y-4">
                    {reports.map(report => (
                      <ReportCard
                        key={report.id}
                        report={report}
                        onModerate={handleModerate}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Verification Tab */}
          <TabsContent value="verification" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Verification Applications</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center py-8 text-gray-500">Loading...</p>
                ) : verificationApps.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No pending applications</p>
                ) : (
                  <div className="space-y-4">
                    {verificationApps.map(app => (
                      <VerificationCard
                        key={app.id}
                        application={app}
                        onReview={handleVerificationReview}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rewards Tab */}
          <TabsContent value="rewards" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Engagement Rewards Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="font-medium">Enable Auto-Rewards</label>
                  <input
                    type="checkbox"
                    checked={rewardSettings.enabled}
                    onChange={(e) => setRewardSettings({...rewardSettings, enabled: e.target.checked})}
                    className="w-4 h-4"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Likes Threshold</label>
                  <Input
                    type="number"
                    value={rewardSettings.likes_threshold}
                    onChange={(e) => setRewardSettings({...rewardSettings, likes_threshold: parseInt(e.target.value)})}
                    placeholder="100"
                  />
                  <p className="text-xs text-gray-500 mt-1">Posts need this many likes to qualify</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Reward Amount (MTR)</label>
                  <Input
                    type="number"
                    value={rewardSettings.reward_amount}
                    onChange={(e) => setRewardSettings({...rewardSettings, reward_amount: parseFloat(e.target.value)})}
                    placeholder="10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Max Rewards Per Day</label>
                  <Input
                    type="number"
                    value={rewardSettings.max_rewards_per_day}
                    onChange={(e) => setRewardSettings({...rewardSettings, max_rewards_per_day: parseInt(e.target.value)})}
                    placeholder="50"
                  />
                  <p className="text-xs text-gray-500 mt-1">Limit daily reward payouts from pool</p>
                </div>

                <Button onClick={saveRewardSettings}>
                  Save Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="ai" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  AI Features Control
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <div>
                    <h3 className="font-semibold">AI Features Status</h3>
                    <p className="text-sm text-gray-600">
                      {aiSettings.enabled ? 'AI is enabled for all users' : 'AI is currently paused'}
                    </p>
                  </div>
                  <Button
                    variant={aiSettings.enabled ? 'destructive' : 'default'}
                    onClick={() => updateAISettings({ enabled: !aiSettings.enabled })}
                  >
                    {aiSettings.enabled ? 'Pause AI' : 'Enable AI'}
                  </Button>
                </div>

                {/* Rate Limiting */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Rate Limiting</h3>
                  
                  <div className="grid gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Daily Limit (Free Users)
                      </label>
                      <Input
                        type="number"
                        value={aiSettings.daily_limit_free}
                        onChange={(e) => setAiSettings({...aiSettings, daily_limit_free: parseInt(e.target.value) || 0})}
                        min="0"
                        placeholder="10"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Set to -1 for unlimited. 0 to disable. Recommended: 10-20 calls/day
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Daily Limit (Premium Users)
                      </label>
                      <Input
                        type="number"
                        value={aiSettings.daily_limit_premium}
                        onChange={(e) => setAiSettings({...aiSettings, daily_limit_premium: parseInt(e.target.value) || 0})}
                        min="-1"
                        placeholder="-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        -1 = unlimited (recommended for premium/admin)
                      </p>
                    </div>

                    <Button onClick={() => updateAISettings({
                      daily_limit_free: aiSettings.daily_limit_free,
                      daily_limit_premium: aiSettings.daily_limit_premium
                    })}>
                      Save Rate Limits
                    </Button>
                  </div>
                </div>

                {/* Usage Statistics */}
                {aiStats && (
                  <div className="space-y-4">
                    <h3 className="font-semibold">Usage Statistics (Last 7 Days)</h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
                        <p className="text-sm text-gray-600">Total AI Calls</p>
                        <p className="text-2xl font-bold">{aiStats.total_calls}</p>
                      </div>
                      <div className="p-4 bg-green-50 dark:bg-green-900 rounded-lg">
                        <p className="text-sm text-gray-600">Unique Users</p>
                        <p className="text-2xl font-bold">{aiStats.unique_users}</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">Daily Breakdown</h4>
                      {aiStats.daily_breakdown.slice(0, 7).map(day => (
                        <div key={day.date} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                          <span className="text-sm">{day.date}</span>
                          <div className="flex gap-4 text-sm">
                            <span>{day.calls} calls</span>
                            <span className="text-gray-500">{day.unique_users} users</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cost Warning */}
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">⚠️ Cost Warning</p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    All AI calls use YOUR Emergent Universal Key credits. Monitor your balance in Profile → Universal Key.
                    Consider enabling auto-top-up to avoid service interruptions.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="creator" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Creator Revenue Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <div>
                    <h3 className="font-semibold">Creator Earnings Status</h3>
                    <p className="text-sm text-gray-600">
                      {creatorConfig.enabled ? 'Creators can earn from posts' : 'Creator earnings paused'}
                    </p>
                  </div>
                  <Button
                    variant={creatorConfig.enabled ? 'destructive' : 'default'}
                    onClick={() => updateCreatorSettings({ enabled: !creatorConfig.enabled })}
                  >
                    {creatorConfig.enabled ? 'Disable' : 'Enable'}
                  </Button>
                </div>

                {/* Revenue Rates */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Revenue Rates</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Ad Revenue (per 1000 views)
                      </label>
                      <Input
                        type="number"
                        step="0.1"
                        value={creatorConfig.ad_revenue_per_1000_views}
                        onChange={(e) => setCreatorConfig({...creatorConfig, ad_revenue_per_1000_views: parseFloat(e.target.value)})}
                        placeholder="1.0"
                      />
                      <p className="text-xs text-gray-500 mt-1">MTR tokens per 1000 ad impressions</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Likes Bonus (per 100 likes)
                      </label>
                      <Input
                        type="number"
                        step="0.1"
                        value={creatorConfig.likes_bonus_per_100}
                        onChange={(e) => setCreatorConfig({...creatorConfig, likes_bonus_per_100: parseFloat(e.target.value)})}
                        placeholder="0.5"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Comments Bonus (per 10 comments)
                      </label>
                      <Input
                        type="number"
                        step="0.1"
                        value={creatorConfig.comments_bonus_per_10}
                        onChange={(e) => setCreatorConfig({...creatorConfig, comments_bonus_per_10: parseFloat(e.target.value)})}
                        placeholder="0.3"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Shares Bonus (per 10 shares)
                      </label>
                      <Input
                        type="number"
                        step="0.1"
                        value={creatorConfig.shares_bonus_per_10}
                        onChange={(e) => setCreatorConfig({...creatorConfig, shares_bonus_per_10: parseFloat(e.target.value)})}
                        placeholder="0.5"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Tip Revenue Share (creator gets)
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        value={creatorConfig.tip_revenue_share}
                        onChange={(e) => setCreatorConfig({...creatorConfig, tip_revenue_share: parseFloat(e.target.value)})}
                        placeholder="0.90"
                      />
                      <p className="text-xs text-gray-500 mt-1">0.90 = 90% to creator, 10% platform fee</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Minimum Payout (MTR)
                      </label>
                      <Input
                        type="number"
                        value={creatorConfig.minimum_payout}
                        onChange={(e) => setCreatorConfig({...creatorConfig, minimum_payout: parseFloat(e.target.value)})}
                        placeholder="10"
                      />
                    </div>
                  </div>

                  <Button onClick={() => updateCreatorSettings(creatorConfig)}>
                    Save Creator Settings
                  </Button>
                </div>

                {/* Top Creators */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Top Earning Creators</h3>
                  {topCreators.length > 0 ? (
                    <div className="space-y-2">
                      {topCreators.map((creator, index) => (
                        <div key={creator.user_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-lg">#{index + 1}</span>
                            <div>
                              <p className="font-medium">{creator.display_name || creator.username}</p>
                              <p className="text-xs text-gray-500">@{creator.username}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">{creator.total_earned} MTR</p>
                            <p className="text-xs text-gray-500">{creator.posts_count} posts</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-4">No creators yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="referral" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Referral Revenue Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <div>
                    <h3 className="font-semibold">Referral Program Status</h3>
                    <p className="text-sm text-gray-600">
                      {referralConfig.enabled ? 'Referral commissions active' : 'Referral program paused'}
                    </p>
                  </div>
                  <Button
                    variant={referralConfig.enabled ? 'destructive' : 'default'}
                    onClick={() => updateReferralSettings({ enabled: !referralConfig.enabled })}
                  >
                    {referralConfig.enabled ? 'Disable' : 'Enable'}
                  </Button>
                </div>

                {/* Commission Rates */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Commission Rates</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Transaction Commission (%)
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        value={referralConfig.transaction_commission}
                        onChange={(e) => setReferralConfig({...referralConfig, transaction_commission: parseFloat(e.target.value)})}
                        placeholder="0.05"
                      />
                      <p className="text-xs text-gray-500 mt-1">0.05 = 5% of referred user transactions</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Deposit Commission (%)
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        value={referralConfig.deposit_commission}
                        onChange={(e) => setReferralConfig({...referralConfig, deposit_commission: parseFloat(e.target.value)})}
                        placeholder="0.02"
                      />
                      <p className="text-xs text-gray-500 mt-1">0.02 = 2% of deposits</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Purchase Commission (%)
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        value={referralConfig.purchase_commission}
                        onChange={(e) => setReferralConfig({...referralConfig, purchase_commission: parseFloat(e.target.value)})}
                        placeholder="0.10"
                      />
                      <p className="text-xs text-gray-500 mt-1">0.10 = 10% of purchases</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Signup Bonus (MTR)
                      </label>
                      <Input
                        type="number"
                        value={referralConfig.signup_bonus}
                        onChange={(e) => setReferralConfig({...referralConfig, signup_bonus: parseFloat(e.target.value)})}
                        placeholder="5"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        First Deposit Bonus (MTR)
                      </label>
                      <Input
                        type="number"
                        value={referralConfig.first_deposit_bonus}
                        onChange={(e) => setReferralConfig({...referralConfig, first_deposit_bonus: parseFloat(e.target.value)})}
                        placeholder="10"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Minimum Payout (MTR)
                      </label>
                      <Input
                        type="number"
                        value={referralConfig.minimum_payout}
                        onChange={(e) => setReferralConfig({...referralConfig, minimum_payout: parseFloat(e.target.value)})}
                        placeholder="50"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={referralConfig.lifetime_commissions}
                        onChange={(e) => setReferralConfig({...referralConfig, lifetime_commissions: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium">Lifetime Commissions</span>
                    </label>
                    <p className="text-xs text-gray-500">Referrers earn forever from their referrals</p>
                  </div>

                  <Button onClick={() => updateReferralSettings(referralConfig)}>
                    Save Referral Settings
                  </Button>
                </div>

                {/* Top Referrers */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Top Referrers</h3>
                  {topReferrers.length > 0 ? (
                    <div className="space-y-2">
                      {topReferrers.map((referrer, index) => (
                        <div key={referrer.referrer_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-lg">#{index + 1}</span>
                            <div>
                              <p className="font-medium">{referrer.display_name || referrer.username}</p>
                              <p className="text-xs text-gray-500">@{referrer.username} · {referrer.code}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">{referrer.total_earned} MTR</p>
                            <p className="text-xs text-gray-500">{referrer.total_signups} signups</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-4">No referrers yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Connect Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Additional settings coming soon...</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}

function ReportCard({ report, onModerate }) {
  const [showActions, setShowActions] = useState(false);
  const [notes, setNotes] = useState('');

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <Badge variant={report.category === 'spam' ? 'destructive' : 'default'}>
            {report.category}
          </Badge>
          <p className="font-semibold mt-2">{report.reason}</p>
          <p className="text-sm text-gray-600">
            Reported by @{report.reporter_username}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowActions(!showActions)}
        >
          {showActions ? 'Hide' : 'Actions'}
        </Button>
      </div>

      {report.details && (
        <p className="text-sm text-gray-700 mb-3">{report.details}</p>
      )}

      {report.target_details && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded p-3 mb-3">
          <p className="text-sm font-medium">Target: {report.target_type}</p>
          {report.target_type === 'user' && (
            <p className="text-sm">@{report.target_details.username}</p>
          )}
          {report.target_type === 'post' && (
            <p className="text-sm">{report.target_details.content?.substring(0, 100)}...</p>
          )}
        </div>
      )}

      {showActions && (
        <div className="space-y-3 pt-3 border-t">
          <Textarea
            placeholder="Admin notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => onModerate(report.id, 'dismiss', notes)}>
              <XCircle className="w-4 h-4 mr-1" />
              Dismiss
            </Button>
            <Button size="sm" variant="default" onClick={() => onModerate(report.id, 'warn', notes)}>
              <AlertTriangle className="w-4 h-4 mr-1" />
              Warn
            </Button>
            <Button size="sm" variant="destructive" onClick={() => onModerate(report.id, 'suspend', notes)}>
              <Ban className="w-4 h-4 mr-1" />
              Suspend (7d)
            </Button>
            <Button size="sm" variant="destructive" onClick={() => onModerate(report.id, 'remove_content', notes)}>
              <XCircle className="w-4 h-4 mr-1" />
              Remove Content
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function VerificationCard({ application, onReview }) {
  const [showDetails, setShowDetails] = useState(false);
  const [notes, setNotes] = useState('');
  const stats = application.user_stats || {};

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold">@{application.username}</span>
            <Badge>{application.verification_type}</Badge>
          </div>
          <p className="text-sm text-gray-600 mt-1">{application.email}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowDetails(!showDetails)}
        >
          {showDetails ? 'Hide' : 'Details'}
        </Button>
      </div>

      {showDetails && (
        <>
          <div className="bg-gray-50 dark:bg-gray-800 rounded p-3 mb-3">
            <p className="text-sm font-medium mb-2">User Stats</p>
            <div className="grid grid-cols-4 gap-2 text-sm">
              <div>
                <div className="font-semibold">{stats.followers || 0}</div>
                <div className="text-gray-600">Followers</div>
              </div>
              <div>
                <div className="font-semibold">{stats.posts || 0}</div>
                <div className="text-gray-600">Posts</div>
              </div>
              <div>
                <div className="font-semibold">{stats.total_likes || 0}</div>
                <div className="text-gray-600">Total Likes</div>
              </div>
              <div>
                <div className="font-semibold">{stats.account_age_days || 0}d</div>
                <div className="text-gray-600">Account Age</div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <p className="text-sm font-medium mb-1">Reason:</p>
            <p className="text-sm text-gray-700">{application.reason}</p>
          </div>

          {application.social_links && application.social_links.length > 0 && (
            <div className="mb-3">
              <p className="text-sm font-medium mb-1">Social Links:</p>
              {application.social_links.map((link, i) => (
                <a
                  key={i}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline block"
                >
                  {link}
                </a>
              ))}
            </div>
          )}

          <Textarea
            placeholder="Admin notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="mb-3"
          />

          <div className="flex gap-2">
            <Button
              size="sm"
              variant="default"
              onClick={() => onReview(application.id, true, notes)}
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => onReview(application.id, false, notes)}
            >
              <XCircle className="w-4 h-4 mr-1" />
              Reject
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
